// usePersistentDraft — the composer's never-lose-a-keystroke seam
// (Brief 5 / point 15, the comms artery).
//
// Anything the user types or dictates into a composer is mirrored to
// `localStorage` (debounced) on every change and RESTORED synchronously on
// mount. Reload, re-open, or crash mid-typing → the text is exactly where it
// was. Keyed per thread/composer (point 15: "Keyed per thread") so two open
// channels never share a draft.
//
// Drop-in for `useState<string>`: returns `[draft, setDraft, clearDraft]`.
// `clearDraft()` wipes BOTH the state and the persisted copy, called only after
// the send is safely handed to the outbox (so we never optimistically clear and
// then lose the words). On unmount any pending debounced write is flushed, so a
// fast unmount (route change) can't drop the last keystroke.

import { useCallback, useEffect, useRef, useState } from "react";
import { draftKey, readRaw, removeRaw, writeRaw } from "../model/persistence";

/** How long to wait after the last keystroke before persisting. Small enough
 * that a reload feels lossless, large enough not to write on every character.
 * A pending write is always flushed on clear + unmount, so the debounce only
 * delays the BACKUP, never risks the data. */
const DEBOUNCE_MS = 200;

/** A `useState`-style setter: a value, or an updater of the previous value
 * (so a dictation handler can `setDraft((d) => append(d, fragment))`). */
export type DraftSetter = (next: string | ((prev: string) => string)) => void;

export interface PersistentDraft {
  /** The live draft text. */
  draft: string;
  /** Update the draft (persisted, debounced). Accepts a value or an updater. */
  setDraft: DraftSetter;
  /** Clear the draft + its persisted copy (call AFTER the send is queued). */
  clearDraft: () => void;
}

/**
 * @param scope a stable per-composer id — typically the thread id; a non-thread
 *   composer passes its own (e.g. `"global"` or a share token). When `scope` is
 *   null/empty the hook degrades to a plain in-memory draft with NO persistence
 *   (e.g. before a thread id is known), so it's always safe to call.
 */
export function usePersistentDraft(scope: string | number | null): PersistentDraft {
  const key = scope === null || scope === "" ? null : draftKey(String(scope));

  // Restore synchronously on first render (lazy initializer) so a reload paints
  // the saved text immediately — no empty flash, no effect round-trip.
  const [draft, setDraftState] = useState<string>(() =>
    key ? (readRaw(key) ?? "") : "",
  );

  // Keep the latest key + value in refs so the unmount/flush path uses current
  // values without re-subscribing the effect on every keystroke.
  const keyRef = useRef(key);
  const draftRef = useRef(draft);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  keyRef.current = key;
  draftRef.current = draft;

  // When the scope CHANGES (switching channels, or a null scope resolving to a
  // real thread id), hand off the draft correctly — never lose text:
  //   1. Commit the OLD scope's current text to its OWN key (and cancel its
  //      pending debounce, so a stale timer can't later resurrect it), so the
  //      old channel keeps what was typed.
  //   2. For the NEW scope: if it has a saved draft, restore it; otherwise, if
  //      we came from a NULL scope (persistence was off while the id resolved),
  //      KEEP the in-memory text and persist it under the new key — input typed
  //      before the thread id was known must survive. Only a real→real switch
  //      with no saved draft clears the composer.
  const lastKey = useRef(key);
  useEffect(() => {
    const prevKey = lastKey.current;
    if (prevKey === key) return;
    lastKey.current = key;

    // 1. Flush + cancel the old scope's pending write to ITS key.
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const carried = draftRef.current;
    if (prevKey) {
      if (carried === "") removeRaw(prevKey);
      else writeRaw(prevKey, carried);
    }

    // 2. Hydrate the new scope.
    if (!key) {
      // No persistence target; keep showing whatever was there (in-memory only).
      return;
    }
    const saved = readRaw(key);
    if (saved != null) {
      setDraftState(saved);
      draftRef.current = saved;
    } else if (!prevKey && carried !== "") {
      // null → real: carry the unsaved in-memory text forward and persist it.
      writeRaw(key, carried);
      // state already holds `carried`; nothing to set.
    } else {
      setDraftState("");
      draftRef.current = "";
    }
  }, [key]);

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const k = keyRef.current;
    if (!k) return;
    const v = draftRef.current;
    if (v === "") removeRaw(k);
    else writeRaw(k, v);
  }, []);

  const setDraft = useCallback<DraftSetter>((next) => {
    // Resolve an updater against the current value (ref-backed so this stays a
    // stable callback) before persisting, so storage gets the real next text.
    const resolved =
      typeof next === "function" ? next(draftRef.current) : next;
    setDraftState(resolved);
    draftRef.current = resolved;
    const k = keyRef.current;
    if (!k) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      if (resolved === "") removeRaw(k);
      else writeRaw(k, resolved);
    }, DEBOUNCE_MS);
  }, []);

  const clearDraft = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setDraftState("");
    draftRef.current = "";
    const k = keyRef.current;
    if (k) removeRaw(k);
  }, []);

  // Flush any pending debounced write on unmount so the last keystroke before a
  // route change / close is never lost.
  useEffect(() => flush, [flush]);

  return { draft, setDraft, clearDraft };
}
